/**
 * Browser → API server → blob storage.
 *
 * Every upload the website makes goes through here. The media layer can also
 * mint a direct-to-blob SAS (`createMediaUpload`), and that path is faster —
 * the bytes never touch the API process — but it puts a write-capable storage
 * URL in reach of page scripts and makes uploading depend on the storage
 * account's CORS rules. Machine clients like the studio app still use it;
 * browsers do not.
 *
 * The digest probe is what keeps that affordable. Direct-to-blob callers learn
 * "we already have these bytes" from the intent response before transferring;
 * here the transfer would be over before anyone could ask, so we ask first.
 */

import { apiFetch } from '../apiFetch';

const ASSET_PATH = '/api/upload/asset';

export interface UploadedAsset {
  assetId: string;
  sha256: string;
  /** True when the tenant already held these bytes and nothing was transferred. */
  deduped: boolean;
}


/** SHA-256 of a Blob, hex. Requires a secure context — https or localhost. */
export async function sha256Hex(blob: Blob): Promise<string> {
  if (!window.crypto?.subtle) {
    throw new Error(
      'Secure context required: image hashing needs https (or localhost). ' +
        'Uploading over a plain-http LAN address will not work.'
    );
  }
  const digest = await window.crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Ask whether this account already holds these bytes. */
async function probeDigest(accountId: string, sha256: string): Promise<string | null> {
  const params = new URLSearchParams({ accountId, sha256 });
  const res = await apiFetch(`${ASSET_PATH}?${params}`);
  if (res.status === 404) return null;
  if (!res.ok) return null; // a failed probe is not a failed upload — just transfer
  const body = await res.json();
  return body?.assetId ?? null;
}

export interface UploadOptions {
  filename?: string;
  width?: number | null;
  height?: number | null;
  captureMeta?: Record<string, unknown> | null;
  /** Pass a digest you already computed to skip hashing the blob twice. */
  sha256?: string;
}

/**
 * Put one image in the media library and return its asset id.
 *
 * Idempotent by content: the same bytes always resolve to the same asset, so a
 * retry after a dropped response costs a round trip rather than creating a
 * duplicate.
 */
export async function uploadAsset(
  accountId: string,
  blob: Blob,
  opts: UploadOptions = {}
): Promise<UploadedAsset> {
  const sha256 = opts.sha256 ?? (await sha256Hex(blob));

  const existing = await probeDigest(accountId, sha256);
  if (existing) return { assetId: existing, sha256, deduped: true };

  const form = new FormData();
  form.append('accountId', accountId);
  if (opts.filename) form.append('filename', opts.filename);
  if (opts.width != null) form.append('width', String(opts.width));
  if (opts.height != null) form.append('height', String(opts.height));
  if (opts.captureMeta) form.append('captureMeta', JSON.stringify(opts.captureMeta));
  form.append('file', blob, opts.filename || 'upload.jpg');

  const res = await apiFetch(ASSET_PATH, {
    method: 'POST',
    body: form,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Upload failed (${res.status})`);
  }

  const body = await res.json();
  if (!body?.assetId) throw new Error('Server did not return an asset id');
  return { assetId: body.assetId, sha256: body.sha256 ?? sha256, deduped: Boolean(body.deduped) };
}

export interface AssetBytes {
  blob: Blob;
  /** Calibration profile the photo was captured under, if any. */
  captureMeta: Record<string, unknown> | null;
}

/**
 * Fetch an asset's pixels for editing.
 *
 * Through the API server, not from blob storage: the blobs are readable by URL
 * but send no CORS headers, so a fetch() from a page is blocked and a canvas
 * drawn from an <img> would be tainted — `toBlob` on it throws. The browser
 * genuinely cannot get at these bytes any other way.
 *
 * Defaults to the ORIGINAL. Editing the current rendition would compound every
 * previous crop; sourcing the immutable original makes each crop a fresh
 * decision about the same photograph.
 */
export async function fetchAssetBytes(
  accountId: string,
  assetId: string,
  kind: 'original' | 'canonical' | 'web' | 'thumb' = 'original'
): Promise<AssetBytes> {
  const params = new URLSearchParams({ accountId, kind });
  const res = await apiFetch(`${ASSET_PATH}/${assetId}/bytes?${params}`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Could not read the image (${res.status})`);
  }

  // captureMeta rides in a header so the body stays raw image bytes.
  let captureMeta: Record<string, unknown> | null = null;
  const raw = res.headers.get('X-Capture-Meta');
  if (raw) {
    try {
      captureMeta = JSON.parse(decodeURIComponent(raw));
    } catch {
      /* a malformed profile is not worth failing the edit over */
    }
  }

  return { blob: await res.blob(), captureMeta };
}

/**
 * Store an edited version of an asset as a new rendition.
 *
 * Non-destructive by construction: the server supersedes the current rendition
 * of this kind rather than overwriting it, and refuses to touch `original`. A
 * bad crop costs a re-crop, never the photograph.
 */
export async function saveRendition(
  accountId: string,
  assetId: string,
  blob: Blob,
  opts: {
    kind?: string;
    width?: number | null;
    height?: number | null;
    transform?: Record<string, unknown> | null;
  } = {}
): Promise<void> {
  const form = new FormData();
  form.append('accountId', accountId);
  form.append('kind', opts.kind || 'canonical');
  if (opts.width != null) form.append('width', String(opts.width));
  if (opts.height != null) form.append('height', String(opts.height));
  if (opts.transform) form.append('transform', JSON.stringify(opts.transform));
  form.append('file', blob, `${assetId}-${opts.kind || 'canonical'}.jpg`);

  const res = await apiFetch(`${ASSET_PATH}/${assetId}/rendition`, {
    method: 'POST',
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || `Could not save the edit (${res.status})`);
  }
}

/** Read an image Blob's pixel dimensions, or nulls when it cannot be decoded. */
export async function imageDimensions(
  blob: Blob
): Promise<{ width: number | null; height: number | null }> {
  try {
    const bitmap = await createImageBitmap(blob);
    const dims = { width: bitmap.width, height: bitmap.height };
    bitmap.close?.();
    return dims;
  } catch {
    return { width: null, height: null };
  }
}
